import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { AxiosRequestConfig } from 'axios';
import axiosInstance from 'config/axios';
import { Advisory } from 'interfaces/advisory';
import { AdvisoryBasicModeReason } from 'interfaces/advisory-basic-mode-reason';
import { BasicModeAdvisoryReason } from 'interfaces/basic-mode-advisory-reason';
import { PaginationResponse } from 'interfaces/pagination-response';

/**
 * @description Advisory response type
 * @type {AdvisoryResponse}
 */
export type AdvisoryResponse = PaginationResponse<Advisory>;

/**
 * @description Advisory service
 * @type {AdvisoryService}
 */
const AdvisoryService = {
  /**
   * @description Create advisory
   * @param {Partial<Advisory>} data - Advisory data
   * @returns {Promise<Advisory>} Advisory
   */
  create: (data: Partial<Advisory>) => {
    return axiosInstance.post<Advisory>('/v1/advisories', data);
  },

  /**
   * @description Update advisory
   * @param {string | number} id - Advisory id
   * @param {Partial<Advisory>} data - Advisory data
   * @returns {Promise<Advisory>} Advisory
   */
  update: (id: string | number, data: Partial<Advisory>) => {
    return axiosInstance.patch<Advisory>(`/v1/advisories/${id}`, data);
  },

  /**
   * @description Find advisory
   * @param {string | number} id - Advisory id
   * @param {AxiosRequestConfig} config - Axios request config
   * @returns {Promise<Advisory>} Advisory
   */
  find: (id: string | number, config?: AxiosRequestConfig) => {
    return axiosInstance.get<Advisory>(`/v1/advisories/${id}`, config);
  },

  /**
   * @description Delete advisory
   * @param {string | number} id - Advisory id
   * @returns {Promise<Advisory>} Advisory
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<Advisory>(`/v1/advisories/${id}`);
  },

  /**
   * @description Get advisories
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<AdvisoryResponse>} Advisory response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<AdvisoryResponse>('/v1/advisories', {
      params: queryParams,
    });
  },

  /**
   * @description Remove reason
   * @param {string | number} advisoryId - Advisory id
   * @param {string | number} reasonId - Reason id
   * @returns {Promise<AdvisoryBasicModeReason>} Advisory basic mode advisory reason
   */
  removeReason: (advisoryId: string | number, reasonId: string | number) => {
    return axiosInstance.delete<AdvisoryBasicModeReason>(
      `/v1/advisories/${advisoryId}/basic-mode-advisory-reasons/${reasonId}`,
    );
  },

  /**
   * @description Assign reason
   * @param {string | number} advisoryId - Advisory id
   * @param {string | number} reasonId - Reason id
   * @returns {Promise<AdvisoryBasicModeReason>} Advisory basic mode advisory reason
   */
  assignReason: (advisoryId: string | number, reasonId: string | number) => {
    return axiosInstance.post<AdvisoryBasicModeReason>(
      `/v1/advisories/${advisoryId}/basic-mode-advisory-reasons/${reasonId}`,
    );
  },

  /**
   * @description Get reasons
   * @param {string | number} advisoryId - Advisory id
   * @returns {Promise<BasicModeAdvisoryReason[]>} Advisory basic mode advisory reason
   */
  getReasons: (advisoryId: string | number) => {
    return axiosInstance.get<BasicModeAdvisoryReason[]>(
      `/v1/advisories/${advisoryId}/basic-mode-advisory-reasons`,
    );
  },
};

export default AdvisoryService;
